/**
 * Utilitaire pour la pré-génération PDF en arrière-plan
 * Appelle le backend pour générer le PDF Factur-X (React-PDF server-side)
 * après sauvegarde de facture/devis
 */

import { getAccessToken } from '../services/tokenManager';
import { logger } from './logger';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

/**
 * Pré-génère le PDF Factur-X côté backend (React-PDF server-side)
 * Le backend stocke le PDF pour envoi instantané ultérieur
 *
 * @param invoiceId - ID de la facture
 * @returns Promise<boolean> - true si succès, false sinon
 */
export async function generateAndStorePdfForInvoice(
  _invoiceData: any,
  _company: any,
  invoiceId: number | string
): Promise<boolean> {
  try {
    logger.log('🚀 Pré-génération PDF backend pour facture', invoiceId);

    const token = getAccessToken();
    if (!token) {
      throw new Error('Token d\'authentification manquant');
    }

    // Appeler l'endpoint backend qui génère le PDF via React-PDF (server-side)
    // GET /invoices/:id/pdf — génère à la demande, pas besoin de DOM
    const response = await fetch(`${API_URL}/billing/invoices/${invoiceId}/pdf`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP ${response.status}`);
    }

    logger.log('✅ PDF pré-généré avec succès pour facture', invoiceId);
    return true;
  } catch (error: any) {
    logger.error('❌ Erreur pré-génération PDF:', error);
    // Ne pas notifier l'utilisateur car c'est en arrière-plan
    // Le PDF sera régénéré à la demande si nécessaire
    return false;
  }
}

/**
 * Pré-génère le PDF en arrière-plan de manière asynchrone (fire-and-forget)
 * Ne bloque pas l'UI et ne notifie pas l'utilisateur
 *
 * @param invoiceData - Données de la facture (non utilisé, conservé pour compatibilité)
 * @param company - Données de l'entreprise (non utilisé, conservé pour compatibilité)
 * @param invoiceId - ID de la facture
 */
export function generatePdfInBackground(
  invoiceData: any,
  company: any,
  invoiceId: number | string
): void {
  // Fire-and-forget: lance la pré-génération sans attendre le résultat
  generateAndStorePdfForInvoice(invoiceData, company, invoiceId)
    .then(success => {
      if (success) {
        logger.log('✨ PDF pré-généré disponible pour envoi instantané');
      }
    })
    .catch(err => {
      logger.warn('⚠️ Pré-génération PDF échouée (retry à l\'envoi):', err.message);
    });
}
